import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router, useNavigate } from "react-router-dom";
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import App from "./App";
import "./index.css";
import "@msi/cobalt-styles/cobalt-light-core.css";
import "./dark-theme-overrides.css";

// Suppress MSI Cobalt icon defaultProps warnings in development
if (process.env.NODE_ENV === 'development') {
  const originalError = console.error;
  console.error = (...args) => {
    if (typeof args[0] === 'string' && args[0].includes('Support for defaultProps will be removed')) {
      return; // Suppress defaultProps warnings
    }
    originalError.call(console, ...args);
  };
}

const redirectUri = process.env.REACT_APP_OKTA_REDIRECT_URI!;
const issuer = process.env.REACT_APP_OKTA_ISSUER!;
const clientId = process.env.REACT_APP_OKTA_CLIENT_ID!;
const appBaseUrl = process.env.REACT_APP_OKTA_APP_BASE_URL!;

if (process.env.NODE_ENV !== 'production') {
  console.log("Attempting OktaAuth init. Variables:");
  console.log("Environment:", process.env.NODE_ENV);
  console.log("Issuer:", issuer);
  console.log("Client ID:", clientId);
  console.log("Redirect URI:", redirectUri);   
  console.log("App Base URL:", appBaseUrl);
}

let oktaAuth: OktaAuth | null = null;
try {
  oktaAuth = new OktaAuth({
    issuer: issuer,
    clientId: clientId,
    redirectUri: redirectUri,
    scopes: ['openid', 'profile', 'email', 'groups'],
    pkce: true,
  });
  console.log("OktaAuth instance created successfully.");
} catch (error) {
  console.error("FATAL ERROR creating OktaAuth instance:", error);
}

// --- AppWithRouter component to handle restoreOriginalUri with useNavigate ---
const AppWithRouter: React.FC = () => {
  const navigate = useNavigate();

  // Define restoreOriginalUri using window.location.replace for this test
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri?: string) => {
    const targetUri = originalUri || '/';
    console.log(`>>> restoreOriginalUri called. Original URI: ${originalUri}, Target URI: ${targetUri}`);
    try {
      navigate(toRelativeUrl(targetUri, window.location.origin));
      console.log(`>>> navigate(${targetUri}) called successfully.`);
    } catch (error) {
      console.error(">>> Error during navigate in restoreOriginalUri:", error);
    }
  };

  // Check if oktaAuth was successfully created before rendering Security
  if (!oktaAuth) {
    return <div>Error initializing authentication. Please check console.</div>;
  }

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <App />
    </Security>
  );
};

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

if (!oktaAuth) {
   ReactDOM.createRoot(rootElement).render(
     <div>Error initializing authentication. Please check console.</div>
   );
} else {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
     <React.StrictMode>
      <Router>
        <AppWithRouter />
      </Router>
     </React.StrictMode>
  );
}